import { 
    EditOutlined, SaveOutlined, 
    RollbackOutlined, PlusOutlined
} from '@ant-design/icons';
import { 
  LuPanelLeftOpen, LuPanelLeftClose, LuCircleHelp
 } from "react-icons/lu";

import { 
  PH, AU, 
  AT, DM, 
  LK, IN,
  CO, PK,
} from 'country-flag-icons/react/3x2';
import dayjs from "dayjs";
import { 
  CircleQuestionMark, DollarSign, 
  GraduationCap, Headphones, 
  Heart, Settings,
  Users, Target,
  Trophy, Rocket,
  Award, Brain,
  ShoppingBag, Utensils,
  Ticket, Plane,
  FileText, ClipboardCheck,
  Briefcase, UserCheck,
  Send, ThumbsUp,
  CircleCheck,
  Coffee, Wifi,
  Shield, HardDrive,
  Monitor, Smartphone,
  Printer, TrendingUp,
  CreditCard, Package,
  Globe, Building2,
  Clock, Activity,
  Languages, Palette,
  Phone, Cpu,
  Star, Lightbulb,
  Sparkle, Crown,
  Sparkles, Megaphone,
  Headset, Gift,
  Handshake,
  Bug,
  Flame
} from 'lucide-react';
import React from 'react';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMAT_MDY = 'MM-DD-YYYY';
export const TIME_FORMAT = 'HH:mm:ss';
export const TIME_FORMAT_HM = 'hh:mm';
export const TIME_FORMAT_24HM = 'HH:mm';
export const TIME_FORMAT_12HM = 'hh:mm a';
export const SEPARATED_ID = 4;

export const BUTTON_ICONS = {
  Add: PlusOutlined,
  Edit: EditOutlined,
  Save: SaveOutlined,
  Cancel: RollbackOutlined,
  MenuCollapse: LuPanelLeftClose, 
  MenuExpand: LuPanelLeftOpen,
};

export const CLIENT_STATUS_COLORS = {
  TBD: 'light',
  Active: 'primary',
  Inactive: 'dark',
}

export const EMPLOYMENT_STATUS_COLORS = {
  TBD: 'light',
  Active: 'primary',
  Inactive: 'dark',
  Separated: 'dark',
}

export const USABLE_ICONS ={
  CircleQuestionMark: CircleQuestionMark,
  DollarSign: DollarSign,
  FileText: FileText,
  GraduationCap: GraduationCap,
  Headphones: Headphones,
  Settings: Settings,
  Users: Users,
  Target: Target,
  Trophy: Trophy,
  Rocket: Rocket,
  Award: Award, 
  Brain: Brain,
  ShoppingBag: ShoppingBag, 
  Utensils: Utensils,
  Ticket: Ticket, 
  Plane: Plane,
  ClipboardCheck: ClipboardCheck,
  Briefcase: Briefcase, 
  UserCheck: UserCheck,
  Send: Send, 
  ThumbsUp: ThumbsUp,
  CircleCheck: CircleCheck,
  Coffee: Coffee, 
  Wifi: Wifi,
  Shield: Shield, 
  HardDrive: HardDrive,
  Monitor: Monitor, 
  Smartphone: Smartphone,
  Printer: Printer, 
  TrendingUp: TrendingUp,
  CreditCard: CreditCard, 
  Package: Package,
  Globe: Globe, 
  Building2: Building2,
  Clock: Clock,
  Activity: Activity,
  Languages: Languages,
  Phone: Phone, 
  Star: Star,
  Lightbulb: Lightbulb,
  Sparkle: Sparkle,
  Sparkles: Sparkles,
  Heart: Heart,
  Gift: Gift,
}

export const COLOR_OPTIONS = [
  { label: "Red", value: "red" },
  { label: "Orange", value: "orange" },
  { label: "Yellow", value: "yellow" },
  { label: "Green", value: "green" },
  { label: "Blue", value: "blue" },
  { label: "Indigo", value: "indigo" },
  { label: "Violet", value: "violet" },
  { label: "Teal", value: "teal" },
];

export const ANNOUNCEMENT_COLOR_OPTIONS = [
  { 
    label: "Red",     
    value: "red", 
    gradient: "bg-gradient-red-orange500",    
    iconClass: "flat-red",          
    addInfoCard: "red-orange"
  },
  { 
    label: "Blue",    
    value: "blue", 
    gradient: "bg-gradient-blue-cyan500",     
    iconClass: "flat-blue",        
    addInfoCard: "blue-cyan"},
  { 
    label: "Green",   
    value: "green", 
    gradient: "bg-gradient-green-emerald500", 
    iconClass: "flat-green",      
    addInfoCard: "green-emerald"},
  { 
    label: "Purple",  
    value: "purple", 
    gradient: "bg-gradient-purple-pink500",   
    iconClass: "flat-purple",      
    addInfoCard: "purple-pink"
  }
];

export const CATEGORY_CONFIG = {
  "culture-hero": {
    id: 1,
    title: "Culture Hero",
    award: "Culture Award",
    description: "Actively strengthens inclusion, respect, and team spirit.",
    icon: Users,
    colorClass: "culture-hero",
  },
  "values-in-action": {
    id: 2,
    title: "Values in Action",
    award: "Values Award",
    description: "Consistently lives the Hyvid values, even when it's the harder path.",
    icon: Target,
    colorClass: "values-in-action",
  },
  "peak-performer": {
    id: 3,
    title: "Peak Performer",
    award: "Peak Performer",
    description: "Delivers exceptional results with clear, measurable impact.",
    icon: Trophy,
    colorClass: "peak-performer",
  },
  "innovation-trailblazer": {
    id: 4,
    title: "Innovation Trailblazer",
    award: "Innovation Award",
    description: "Challenges the status quo and turns ideas into measurable, repeatable impact.",
    icon: Rocket,
    colorClass: "innovation-trailblazer",
  },
  "ceo-award": {
    id: 5,
    title: "CEO Award",
    award: "CEO Award",
    description:
      "Goes beyond client delivery to create company-wide value and progress.",
    icon: Award,
    colorClass: "ceo-award",
  },
  "ai-adoption-champion": {
    id: 6,
    title: "AI Adoption Champion",
    award: "AI Adoption Award",
    description:
      "Consistently uses approved AI tools to lift productivity and quality.",
    icon: Brain,
    colorClass: "ai-adoption-champion",
  },
};

export const COUNTRY_FLAG_ICONS = {
  PH: <PH title="Philippines" className="rounded-sm w-6 h-4" />,
  AU: <AU title="Australia" className="rounded-sm w-6 h-4" />,
  AT: <AT title="Austria" className="rounded-sm w-6 h-4" />,
  DM: <DM title="Dominica" className="rounded-sm w-6 h-4" />,
  LK: <LK title="Sri Lanka" className="rounded-sm w-6 h-4" />,
  IN: <IN title="India" className="rounded-sm w-6 h-4" />,
  PK: <PK title="Pakistan" className="rounded-sm w-6 h-4" />,
  CO: <CO title="Colombia" className="rounded-sm w-6 h-4" />,
};

export const STAFF_STATUS_COLORS = {
  TBD: 'light',
  Active: 'primary',
  Inactive: 'dark',
  Draft: 'light',
}


export const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  dayjs().month(i).format("MMMM")
);

export const MONTH_OPTIONS = MONTH_NAMES.map((name, i) => ({
  value: i + 1,
  label: name,
}));

export const getDaysInMonth = (month) =>
  dayjs(`2000-${String(month).padStart(2, "0")}-01`).daysInMonth();

export const getDayOptions = (month) =>
  Array.from({ length: getDaysInMonth(month) }, (_, i) => ({
    value: i + 1,
    label: i + 1,
  }));


export const PIPELINE_STEPS = {
  Application: FileText,
  Screening: ClipboardCheck,
  Testing: Target,
  Shortlisting: UserCheck,
  "Client Endorsement": Send,
  "Client Interview": Headset,
  "Client Selection": ThumbsUp,
  References: Award,
  Offer: CircleCheck,
  Onboarding: Briefcase,
}

export const CATEGORY_ICONS = {
  Network: Wifi,
  Account: Shield,
  Software: HardDrive,
  Hardware: Monitor,
  Mobile: Smartphone,
  Printer: Printer,
  "Work-Life Balance": Briefcase,
  Engagement: Coffee,
  Learning: TrendingUp,
  "HR Support": Users,
  Benefits: Shield,
  Billing: CreditCard,
  Contracts: FileText,
  Service: Package,
  Partnerships: Globe,
  Support: Building2,
  Others: Bug,
};

export const DEPARTMENT_ICONS = {
  "Design": Palette,
  "Engineering": Cpu,
  "Executive": Crown,
  "Finance": DollarSign,
  "HR": Users,
  "IT": Monitor,
  "Marketing": Megaphone,
  "Operations": Settings,
  "Sales": Phone,
  "Partner Services": Handshake,
  "General Inquiries": Building2,
  "Customer Service": Headset,
};

export const DEPARTMENT_COLORS = {
  "Design": { bg: "bg-purple-50", icon: "text-purple-700" },
  "Engineering": { bg: "bg-blue-50", icon: "text-blue-700" },
  "Executive": { bg: "bg-slate-50", icon: "text-slate-700" },
  "Finance": { bg: "bg-green-50", icon: "text-green-700" },
  "HR": { bg: "bg-pink-50", icon: "text-pink-700" },
  "IT": { bg: "bg-orange-50", icon: "text-orange-700" },
  "Marketing": { bg: "bg-cyan-50", icon: "text-cyan-600" },
  "Operations": { bg: "bg-gray-50", icon: "text-gray-700" },
  "Sales": { bg: "bg-emerald-50", icon: "text-emerald-700" },
  "Partner Services": { bg: "bg-indigo-50", icon: "text-indigo-700" },
  "General Inquiries": { bg: "bg-amber-50", icon: "text-amber-700" },
  "Customer Service": { bg: "bg-violet-50", icon: "text-violet-700" },
};

export const extractDepartmentData = (messages = [], departmentOptions = []) => {
  if (!departmentOptions.length) return [];

  const messageCountMap = {};

  messages.forEach((msg) => {
    const deptId = msg.tic_department_id;

    if (deptId) {
      messageCountMap[deptId] = (messageCountMap[deptId] || 0) + 1;
    }
  });

  return departmentOptions.map((dept) => ({
    department: dept.label,
    value: dept.value,                 
    department_id: dept.department_id, 
    count: messageCountMap[dept.department_id] || 0,
    icon: DEPARTMENT_ICONS[dept.department_name] || Building2,
    bgColor: DEPARTMENT_COLORS[dept.department_name]?.bg || "bg-gray-50",
    iconColor: DEPARTMENT_COLORS[dept.department_name]?.icon || "text-gray-600",
  }));
};

export const BUTTONSTORE_ICONS = {
  "All Vouchers": 'ShoppingBag',
  "Food": 'Utensils',
  "Shop": 'ShoppingBag',
  "Entertainment": 'Ticket',
  "Travel & Leisure": 'Plane',
  "Health & Wellness": 'Heart',
  "Others": 'ShoppingBag',
};

export const STORE_CATEGORIES = [
  { label: "All Vouchers", value: "all", icon: 'ShoppingBag' },
  { label: "Food", value: "food", icon: 'Utensils' },
  { label: "Shop", value: "shopping", icon: 'ShoppingBag' },
  { label: "Entertainment", value: "entertainment", icon: 'Ticket' },
  { label: "Travel & Leisure", value: "travel", icon: 'Plane' },
  { label: "Health & Wellness", value: "wellness", icon: 'Heart' },
  { label: "Others", value: "wellness", icon: 'ShoppingBag' },
];

export const VOUCHERS = [
  {
    label: "Food",
    value: "food",
    icon: Utensils,
    gradientFrom: "from-yellow-500",
    gradientTo: "to-red-600",
  },
  {
    label: "Others",
    value: "others",
    icon: ShoppingBag,
    gradientFrom: "from-orange-500",
    gradientTo: "to-yellow-600",
  },
  {
    label: "Shop",
    value: "shop",
    icon: ShoppingBag,
    gradientFrom: "from-purple-600",
    gradientTo: "to-pink-600",
  },
  {
    label: "Entertainment",
    value: "entertainment",
    icon: Ticket,
    gradientFrom: "from-red-600",
    gradientTo: "to-red-900",
  },
  {
    label: "Starbucks",
    value: "starbucks",
    icon: Coffee,
    gradientFrom: "from-green-700",
    gradientTo: "to-green-900",
  },
  {
    label: "Amazon",
    value: "amazon",
    icon: ShoppingBag,
    gradientFrom: "from-orange-500",
    gradientTo: "to-yellow-600",
  },
  {
    label: "Netflix",
    value: "netflix",
    icon: Ticket,
    gradientFrom: "from-red-600",
    gradientTo: "to-red-900",
  },
  {
    label: "Spotify",
    value: "spotify",
    icon: Headphones,
    gradientFrom: "from-green-500",
    gradientTo: "to-green-700",
  },
  {
    label: "Grab",
    value: "grab",
    icon: Utensils,
    gradientFrom: "from-green-600",
    gradientTo: "to-emerald-700",
  },
  {
    label: "Airbnb",
    value: "airbnb",
    icon: Plane,
    gradientFrom: "from-pink-500",
    gradientTo: "to-rose-600",
  },
  
  {
    label: "SM Cinema",
    value: "cinema",
    icon: Ticket,
    gradientFrom: "from-blue-800",
    gradientTo: "to-indigo-900",
  },
  {
    label: "Bookdepository Voucher",
    value: "book",
    icon: ShoppingBag,
    gradientFrom: "from-purple-800",
    gradientTo: "to-blue-900",
  },
  {
    label: "Fitness First",
    value: "fitness",
    icon: Heart,
    gradientFrom: "from-red-600",
    gradientTo: "to-orange-600",
  },
  {
    label: "Zalora",
    value: "zalora",
    icon: ShoppingBag,
    gradientFrom: "from-purple-600",
    gradientTo: "to-pink-600",
  },
  {
    label: "Zen Wellness",
    value: "zenwellness",
    icon: Heart,
    gradientFrom: "from-teal-600",
    gradientTo: "to-cyan-600",
  },
  
];

export const normalize = (str = "") =>
  str.toLowerCase().replace(/[\s'"]/g, "");

export const getVoucherMeta = (key) => {
  const safeKey = normalize(key);
  return VOUCHERS.find(v => normalize(v.label) === safeKey) || {};
};

// export const getCountryFlag = (code, className = "w-5 h-3") =>
//   COUNTRY_FLAG_ICONS[code]
//     ? React.cloneElement(COUNTRY_FLAG_ICONS[code], { className })
//     : null;

export const QUICK_FACTS = [
  {
    id: "businessHours",
    label: "Business Hours",
    icon: Clock,
    border: "border-slate-200"
  },
  {
    id: "languages",
    label: "Languages",
    icon: Languages,
    border: "border-slate-200"
  },
  {
    id: "currency",
    label: "Currency",
    icon: DollarSign,
  },
  {
    id: "dialingCode",
    label: "Dialing Code",
    icon: Phone,
    border: "border-slate-200"
  },
  {
    id: "educationLevel",
    label: "Education Level",
    icon: GraduationCap,
    border: "border-slate-200"
  },
  {
    id: "internetSpeed",
    label: "Internet Speed",
    icon: Wifi,
    border: "border-slate-200"
  },
  {
    id: "keyIndustries",
    label: "Key Industries",
    icon: Building2,
    textColor: "text-slate-900",
    border: "border-slate-200"
  },
  {
    id: "population",
    label: "Population",
    icon: Users,
    iconColor: "text-green-600 dark:text-green-500",
    bgColor: "bg-gradient-to-br from-green-50 to-white dark:from-green-900 dark:to-white/10",
    textColor: "text-slate-700",
    border: "border-green-200"
  },
  {
    id: "salaryRange",
    label: "Avg. Salary Range",
    icon: DollarSign,
    iconColor: "text-emerald-600 dark:text-green-500",
    bgColor: "bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900 dark:to-white/10",
    textColor: "text-emerald-600",
    border: "border-emerald-200"
  },
  
];

export const LOCATION_THEMES = {
  Philippines: "blue",
  "Sri Lanka": "green",
  India: "orange",
};

export const getIcon = (code, className = "w-5 h-5") => {

  if (COUNTRY_FLAG_ICONS[code]) {
    return React.cloneElement(COUNTRY_FLAG_ICONS[code], { className });
  }

  switch (code) {
    case "ALL": return <Briefcase className="w-4 h-4 text-slate-600" />;
    case "FINANCE": return <DollarSign className="w-4 h-4 text-slate-600" />;
    case "CUSTOMER": return <Users className="w-4 h-4 text-slate-600" />;
    case "ADMIN": return <Building2 className="w-4 h-4 text-slate-600" />;
    case "SALES": return <TrendingUp className="w-4 h-4 text-slate-600" />;
    case "PROPERTY": return <Building2 className="w-4 h-4 text-slate-600" />;
    case "TECH": return <Activity className="w-4 h-4 text-slate-600" />;
    case "CONSTRUCTION": return <Target className="w-4 h-4 text-slate-600" />;
    case "ECOMMERCE": return <Target className="w-4 h-4 text-slate-600" />;

    default: return null;
  }
};

export const Paragraph = ({ text }) => (
  <p className="text-[12px] sm:text-[13px] text-slate-700 leading-relaxed pb-4">
    {text}
  </p>
);

export const KeyValue = ({ keyText, value }) => (
  <div className="pb-4">
    <p className="text-[12px] sm:text-[13px] text-slate-700 leading-relaxed">{keyText}</p>
    <p className="text-[12px] sm:text-[13px] text-slate-700 leading-relaxed">{value}</p>
  </div>
);


export const List = ({ text, items = [] }) => (
  <div className="pb-4">
    {text && (
      <p className="text-[12px] sm:text-[13px] text-slate-700 leading-relaxed">
        {text}
      </p>
    )}

    <ul className="list-disc list-inside text-[12px] sm:text-[13px] text-slate-700 leading-relaxed">
      {items.map((item, i) =>
        typeof item === "string" ? (
          <li key={i}>{item}</li>
        ) : (
          <li key={i}>
            {item.text}
            <ul className="list-inside pl-2 mt-1">
              {item.items.map((subItem, j) => (
                <li key={j}>â€“ {subItem}</li>
              ))}
            </ul>
          </li>
        )
      )}
    </ul>
  </div>
);

export const SubTitle = ({ text }) => (
  <h5 className="font-semibold text-[13px] sm:text-[14px] mb-4">
    {text}
  </h5>
);

export const TableText = ({ headers = [], rows = [] }) => {
  const headerLine = headers.join(" | ");
  const separatorLine = headers.map(() => "----------------").join("|");
  const rowLines = rows.map((row) => row.join(" | ")).join("\n");

  return (
    <pre className="text-[12px] sm:text-[13px] text-slate-700 leading-relaxed mb-4">
      {`${headerLine}\n${separatorLine}\n${rowLines}`}
    </pre>
  );
};

export const recognitionFilters = [
  { label: "All", value: "all", icon: <Sparkles className="w-3 h-3"/> },
  { 
    label: "Teamwork", 
    value: "teamwork", 
    icon: <Handshake className="w-3 h-3"/>, 
    bg: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-50 dark:bg-blue-900",
    border: "border-blue-200 dark:border-blue-700"
  },
  { 
    label: "Innovation", 
    value: "innovation", 
    icon: <Lightbulb className="w-3 h-3"/>,  
    bg: "from-purple-500 to-pink-500",
    bgColor: "bg-purple-50 dark:bg-purple-900",
    border: "border-purple-200 dark:border-purple-700"
  },
  { 
    label: "Leadership", 
    value: "leadership", 
    icon: <Crown className="w-3 h-3"/>, 
    bg: "from-amber-500 to-orange-500",
    bgColor: "bg-amber-50 dark: dark:bg-amber-900",
    border: "border-amber-200 dark:border-amber-700"
  },
  { 
    label: "Achievement", 
    value: "achievement", 
    icon: <Trophy className="w-3 h-3"/>, 
    bg: "from-green-500 to-emerald-500",
    bgColor: "bg-green-50 dark:bg-green-900",
    border: "border-green-200 dark:border-green-700"
  },
  { 
    label: "Helpfulness", 
    value: "helpfulness", 
    icon: <Heart className="w-3 h-3"/>, 
    bg: "from-pink-500 to-rose-500",
    bgColor: "bg-pink-50 dark:bg-pink-900",
    border: "border-pink-200 dark:border-pink-700"
  },
  { 
    label: "Excellence", 
    value: "excellence", 
    icon: <Flame className="w-3 h-3"/>, 
    bg: "from-red-500 to-orange-500",
    bgColor: "bg-red-50 dark:bg-red-900",
    border: "border-red-200 dark:border-red-700"
  }
];


export const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.width, height: img.height });
        };
        
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        
        img.src = url;
    });
};


export const validateImageAttachment = async (file, options = {}) => {
    const {
        maxSizeMB = 20,
        maxWidth = 8000,
        maxHeight = 8000,
        allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff']
    } = options;
    
    if (!file) {
        return { valid: false, error: 'No file selected' };
    }
    
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
        return { valid: false, error: `File too large. Max size is ${maxSizeMB}MB` };
    }
    
    if (!allowedTypes.includes(file.type)) {
        return { valid: false, error: 'Only image files are allowed (JPG, PNG, GIF, WEBP, BMP, TIFF)' };
    }
    
    try {
        const dimensions = await getImageDimensions(file);
        
        if (dimensions.width > maxWidth || dimensions.height > maxHeight) {
            return { 
                valid: false, 
                error: `Image too large. Max ${maxWidth}x${maxHeight}px`,
                dimensions 
            };
        }
        
        return { valid: true, dimensions };
    } catch (error) {
        return { valid: false, error: 'Invalid or corrupted image file' };
    }
};

export const CORE_VALUE_META = {
  Innovation: {
    icon: "Lightbulb",
    color: "blue",
  },
  Opportunity: {
    icon: "Target",
    color: "green",
  },
  Excellence: {
    icon: "Star",
    color: "amber",
  },
  Integrity: {
    icon: "Shield",
    color: "indigo",
  },
  Connection: {
    icon: "Heart",
    color: "red",
  },
};

export const formatTime = (time) =>
  dayjs(`2000-01-01 ${time}`).format("h:mm A");

export const formatLeaveDate = (from, to) => {
  const start = dayjs(from);
  const end = dayjs(to);

  if (start.isSame(end, "day")) {
      return start.format("MMM DD, YYYY");
  }

  if (start.isSame(end, "month")) {
      return `${start.format("MMM DD")}-${end.format("DD, YYYY")}`;
  }

  return `${start.format("MMM DD, YYYY")} – ${end.format(
      "MMM DD, YYYY"
  )}`;
};

export const calculateTotalDays = (from, to) =>
  dayjs(to).diff(dayjs(from), "day") + 1;

export const ROLE_SUPER_ADMIN = 1;
export const ROLE_ACCT_ADMIN = 2;
export const ROLE_ACCT_USER = 3;
export const ROLE_CLIENT = 4;
export const ROLE_EMPLOYEE = 5;

export const PAYROLL_TYPE_REG = 45;
export const PAYROLL_TYPE_OFFCYCLE = 46;
export const PAYROLL_TYPE_13TH_MONTH = 47;

export const PAY_STAT_DRAFT = 48;
export const PAY_STAT_INPROGRESS = 49;
export const PAY_STAT_VALIDATED = 50;
export const PAY_STAT_APPROVED = 51;
export const PAY_STAT_RELEASED = 52;

export const MODULE_DASHBOARD = 6;
export const MODULE_TEAM_MENU = 7;
export const MODULE_CLIENT = 8;
export const MODULE_EMPLOYEES = 9;
export const MODULE_SALARIES = 10;
export const MODULE_PAYROLL_MENU = 11;
export const MODULE_PAYROLLS = 12;
export const MODULE_SAL_ADJUSTMENTS = 13;
export const MODULE_SAL_DEDUCTIONS = 14;
export const MODULE_SAL_ADDITIONS = 15;
export const MODULE_FINAL_PAYS = 16;
export const MODULE_FINANCE = 17;
export const MODULE_INVOICES = 18;
export const MODULE_MANUAL_JOURNALS = 19;
export const MODULE_CLIENT_EXPENSE= 20;
export const MODULE_INVOICE_ADJUSTMENTS = 21;
export const MODULE_REPORTS = 22;
export const MODULE_ACCESS_MANAGEMENT_MENU = 23;
export const MODULE_ACCESS_MANAGER = 24;
export const MODULE_USERS = 25;
export const MODULE_ACCESS_GROUPS = 26;
export const MODULE_ACCESS_RIGHTS = 27;
export const MODULE_MODULES = 28;
export const MODULE_SETTINGS_MENU = 29;
export const MODULE_HOLIDAYS = 30;
export const MODULE_OPTION_GROUPS = 31;
export const MODULE_OPTIONS = 32;
export const MODULE_PAYCODES = 33;
export const MODULE_USER_GUIDE = 34;
export const MODULE_SYSTEM_CHECK = 35;
export const MODULE_TIMEKEEPING = 36;
export const MODULE_TIMECARDS = 37;
export const MODULE_OVERTIME = 38;
export const MODULE_LEAVE = 39;
export const MODULE_MYPAYSLIPS = 40;
export const MODULE_RECRUITMENT = 41;
export const MODULE_JOB_POSTS = 42;
export const MODULE_RECRUITMENT_OVERVIEW = 43;
export const MODULE_JOBROLES = 44;
export const MODULE_JOBROLE_REQS = 45;
export const MODULE_TRAINING = 46;
export const MODULE_TRAINING_MOD = 47;
export const MODULE_TRAINING_REQ = 48;
export const MODULE_TRAINING_CLASS = 49;
export const MODULE_TRAINING_OVERVIEW = 50;
export const MODULE_TRAINING_AND_DEV = 51;
export const MODULE_TRAINING_MY_COURSES = 52;
export const MODULE_BROWSE_TRAINING = 53;
export const MODULE_CERTIFICATIONS = 54;
export const MODULE_PERFORMANCE_GOALS = 55;
export const MODULE_AWARDS_RECOGNITION = 56;  
export const MODULE_AWARDS = 57; 
export const MODULE_REWARDS = 58; 
export const MODULE_MY_AWARDS = 59;  
export const MODULE_MY_POINTS = 60; 
export const MODULE_RECOG_WALL = 61; 
export const MODULE_APPROVAL_AWARDS = 62; 
export const MODULE_INBOX = 63;
export const MODULE_SUPP_TICKETS = 64;
export const MODULE_MY_DOCS = 65;
export const MODULE_EMP_DETAILS = 66;
export const MODULE_TEAM_OVERVIEW = 67;
export const MODULE_REVIEWS = 68;
export const MODULE_PERF_IMPROVS = 69;
export const MODULE_PRODUCTIVITY = 70;
export const MODULE_LEAVE_REQUESTS = 71;
export const MODULE_TEAM_ABSENCES = 72;
export const MODULE_ATTENDANCE_OVERVIEW = 73;
export const MODULE_HYVIDSTORE = 74;
export const MODULE_OVERTIME_REQUESTS = 75;
export const MODULE_AI_CHAT = 76;
export const MODULE_AI_COPILOT = 77;
export const MODULE_HOUSE_RULES = 0; 
export const MODULE_COUNTRY_GUIDE = 0; 
export const MODULE_TOOLS = 79;
export const MODULE_SN_CALCULATOR = 80;
export const MODULE_APPROVAL_REWARDS = 78; 
export const MODULE_PURCHASE_ORDER = 81;
export const MY_INVOICES = 82;
